import { useMemo, useState } from 'react';
import type { DraftPick, DraftSummary, LeagueBundle } from '../../shared/types';
import {
  Empty,
  ErrorState,
  Legend,
  Loading,
  PageHead,
  PositionTag,
  Section,
} from '../components/common';
import { useDrafts } from '../lib/api';
import { fmtDateTime, ordinal } from '../lib/format';
import { buildTeamViews, teamIndex, type TeamView } from '../lib/league';
import { Link } from '../lib/router';

/**
 * One cell of the board.
 *
 * A pick sits in the column of the slot it ORIGINALLY belonged to, so each
 * column reads as "the picks that started out as this team's". When the roster
 * that actually made the selection differs from the slot's owner, the pick was
 * traded — which is derivable from the pick alone, with no traded-pick lookup.
 */
function BoardCell({
  pick,
  slotOwner,
  teams,
}: {
  pick: DraftPick | undefined;
  slotOwner: TeamView | undefined;
  teams: Map<number, TeamView>;
}) {
  if (!pick) {
    return (
      <td className="board-cell">
        <span className="faint">—</span>
      </td>
    );
  }

  const madeBy = teams.get(pick.rosterId);
  const traded = slotOwner != null && pick.rosterId !== slotOwner.roster_id;

  return (
    <td
      className={`board-cell ${traded ? 'board-traded' : ''}`}
      // Fitting a full league across the viewport means names get clipped, so
      // the untruncated pick is always available on hover.
      title={
        `Pick ${pick.pickNo} — ${pick.name} (${pick.position}, ${pick.nflTeam ?? 'FA'})` +
        (traded && slotOwner ? `\nTraded: ${slotOwner.name} → ${madeBy?.name ?? 'unknown'}` : '')
      }
    >
      <div className="board-pick-no">{pick.pickNo}</div>
      <div className="board-player">
        <PositionTag position={pick.position} />
        <span className="board-name">{pick.name}</span>
      </div>
      <div className="board-meta">
        {pick.nflTeam ?? 'FA'}
        {traded && (
          <span className="board-via" title={`Pick traded to ${madeBy?.name ?? 'another team'}`}>
            → {madeBy?.name ?? `roster ${pick.rosterId}`}
          </span>
        )}
      </div>
    </td>
  );
}

function Board({ draft, teams }: { draft: DraftSummary; teams: Map<number, TeamView> }) {
  // Index by round+slot rather than by pick number. A snake draft reverses
  // direction every round, so laying cells out by (round, slot) renders the
  // board correctly with no reversal arithmetic.
  const grid = useMemo(() => {
    const m = new Map<string, DraftPick>();
    for (const p of draft.picks) m.set(`${p.round}|${p.draftSlot}`, p);
    return m;
  }, [draft.picks]);

  const rounds = Array.from({ length: draft.rounds }, (_, i) => i + 1);
  const slots = draft.slotToRoster;

  return (
    <div className="table-scroll">
      <table className="board">
        <thead>
          <tr>
            <th className="board-round-head">Rd</th>
            {slots.map((rosterId, i) => {
              const t = teams.get(rosterId);
              return (
                <th key={i} className="board-team-head">
                  <div className="board-slot">{i + 1}</div>
                  {t ? (
                    <Link to={`/teams/${t.roster_id}`} title={t.name}>
                      {t.name}
                    </Link>
                  ) : (
                    `Roster ${rosterId}`
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rounds.map((round) => (
            <tr key={round}>
              <td className="board-round">{round}</td>
              {slots.map((rosterId, i) => (
                <BoardCell
                  key={i}
                  pick={grid.get(`${round}|${i + 1}`)}
                  slotOwner={teams.get(rosterId)}
                  teams={teams}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Each team's haul, for reading the draft by team instead of by round. */
function ByTeam({ draft, teams }: { draft: DraftSummary; teams: Map<number, TeamView> }) {
  const grouped = useMemo(() => {
    const m = new Map<number, DraftPick[]>();
    for (const p of draft.picks) {
      const list = m.get(p.rosterId) ?? [];
      list.push(p);
      m.set(p.rosterId, list);
    }
    return [...m.entries()]
      .map(([rosterId, picks]) => ({
        team: teams.get(rosterId),
        rosterId,
        picks: picks.sort((a, b) => a.pickNo - b.pickNo),
      }))
      .sort((a, b) => b.picks.length - a.picks.length || a.rosterId - b.rosterId);
  }, [draft.picks, teams]);

  return (
    <div className="grid grid-3">
      {grouped.map(({ team, rosterId, picks }) => (
        <div className="card card-pad" key={rosterId}>
          <div className="trade-side-name" style={{ marginBottom: 10 }}>
            {team ? (
              <Link to={`/teams/${team.roster_id}`}>{team.name}</Link>
            ) : (
              `Roster ${rosterId}`
            )}
            <span className="pill" style={{ marginLeft: 'auto' }}>
              {picks.length}
            </span>
          </div>
          {picks.map((p) => (
            <div className="player-row" key={p.pickNo}>
              <span className="slot-label">
                {p.round}.{String(((p.pickNo - 1) % Math.max(1, draft.slotToRoster.length)) + 1).padStart(2, '0')}
              </span>
              <PositionTag position={p.position} />
              <span className="player-name">{p.name}</span>
              <span className="player-meta">{p.nflTeam ?? 'FA'}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function DraftRecap({ bundle }: { bundle: LeagueBundle }) {
  const { data, error, loading, reload } = useDrafts();
  const [seasonIdx, setSeasonIdx] = useState(0);
  const [view, setView] = useState<'board' | 'team'>('board');

  const teams = useMemo(() => teamIndex(buildTeamViews(bundle)), [bundle]);

  if (loading) return <Loading label="Loading draft results" />;
  if (error || !data) {
    return <ErrorState message={error ?? 'No draft data returned'} onRetry={reload} />;
  }
  if (data.drafts.length === 0) {
    return (
      <>
        <PageHead title="Draft Recap" />
        <div className="card">
          <Empty>No completed drafts yet.</Empty>
        </div>
      </>
    );
  }

  const draft = data.drafts[Math.min(seasonIdx, data.drafts.length - 1)];
  const isStartup = draft.rounds > 6;

  return (
    <>
      <PageHead
        title="Draft Recap"
        meta={[
          `${draft.season} ${isStartup ? 'startup' : 'rookie'}`,
          `${draft.rounds} rounds`,
          `${draft.type} order`,
          'Columns are the slot’s original owner',
        ]}
      />

      <div className="toolbar">
        {data.drafts.map((d, i) => (
          <button
            key={d.draft_id}
            className={`chip ${i === seasonIdx ? 'on' : ''}`}
            onClick={() => setSeasonIdx(i)}
          >
            {d.season}
            <span className="faint"> {d.rounds}rd</span>
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button
          className={`chip ${view === 'board' ? 'on' : ''}`}
          onClick={() => setView('board')}
        >
          Board
        </button>
        <button className={`chip ${view === 'team' ? 'on' : ''}`} onClick={() => setView('team')}>
          By team
        </button>
      </div>

      <Section
        title={`${draft.season} ${isStartup ? 'Startup' : 'Rookie'} Draft`}
        hint={
          draft.startTime
            ? `${draft.picks.length} picks · ${fmtDateTime(draft.startTime)}`
            : `${draft.picks.length} picks`
        }
      >
        {view === 'board' ? (
          <>
            <Board draft={draft} teams={teams} />
            <Legend
              items={[
                { term: 'Highlighted', def: 'pick was traded; the arrow is who selected' },
                { term: 'Small number', def: 'overall pick' },
              ]}
            />
          </>
        ) : (
          <ByTeam draft={draft} teams={teams} />
        )}
      </Section>

      {draft.picks.length > 0 && (
        <Section title="First Round">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="rank">#</th>
                  <th>Player</th>
                  <th>Pos</th>
                  <th>NFL</th>
                  <th>Drafted by</th>
                  <th>Slot originally</th>
                </tr>
              </thead>
              <tbody>
                {draft.picks
                  .filter((p) => p.round === 1)
                  .map((p) => {
                    const madeBy = teams.get(p.rosterId);
                    const slotOwner = teams.get(draft.slotToRoster[p.draftSlot - 1]);
                    const traded = slotOwner && slotOwner.roster_id !== p.rosterId;
                    return (
                      <tr key={p.pickNo}>
                        <td className={`rank ${p.pickNo === 1 ? 'top' : ''}`}>{p.pickNo}</td>
                        <td style={{ fontWeight: 600 }}>{p.name}</td>
                        <td>
                          <PositionTag position={p.position} />
                        </td>
                        <td className="faint">{p.nflTeam ?? 'FA'}</td>
                        <td>
                          {madeBy ? (
                            <Link to={`/teams/${madeBy.roster_id}`}>{madeBy.name}</Link>
                          ) : (
                            `Roster ${p.rosterId}`
                          )}
                        </td>
                        <td className={traded ? 'bad' : 'faint'}>
                          {traded ? `${slotOwner?.name} (${ordinal(p.draftSlot)})` : 'own'}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </>
  );
}
